import { CloseIcon, DonateIcon, TextJustifyIcon } from '@web/components/atoms/Icons'
import { Box, Flex, Link } from '@web/components/atoms/Layout'
import type { Header as HeaderProps } from '@web/graphql-types'
import { PageButton } from '../PageButton'
import { Logo } from '@web/components/atoms/Logo'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { SocialShare } from '../SocialShare'
import { useMediaQuery } from '@web/components/utils/chakra-hooks'
import { DESKTOP_CONTAINER_QUERY, MenuItemLink, MOBILE_CONTAINER_QUERY } from '../utils'
import { IconButton } from '@web/components/atoms/Button'
import React, { useRef } from 'react'
import { DrawerContent, DrawerContext, DrawerRoot, DrawerTrigger } from '@web/components/atoms/Modal'

const Header = (
  props: HeaderProps & {
    currentUrl?: string
    topOffset?: number
    LinkComponent?: React.FC<React.PropsWithChildren<{ href: string; prefetch?: boolean }>>
    children?: React.ReactNode
  },
) => {
  const [isMobile] = useMediaQuery(['(max-width: 900px)'], { fallback: [] })

  const { styles, mobileStyles } = props
  const backgroundColor = useColorWithOpacity(styles?.backgroundColor, styles?.backgroundColorOpacity, '#FFFFFF')
  const logoAlignment = styles?.logoAlignment ?? 'left'
  const headerRef = useRef<HTMLDivElement>(null)
  const LinkComponent = props.LinkComponent ?? Link

  return (
    <Box
      id="ge-header"
      w="100%"
      position={styles?.position === 'sticky' ? 'sticky' : 'static'}
      top={props.topOffset ? `${props.topOffset}px` : 0}
      zIndex={3}
    >
      <Box
        minH="96px"
        ref={headerRef}
        {...(styles?.id ? { id: styles.id } : {})}
        {...(styles?.class ? { className: styles.class } : {})}
        css={{
          backgroundColor: backgroundColor,
          justifyContent: 'center',
          display: mobileStyles?.display ?? 'flex',
          '@media (min-width: 901px)': {
            display: styles?.display ?? 'flex',
          },
        }}
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          p="16px 24px"
          minH="96px"
          h="100%"
          w="100%"
          maxW={styles?.maxWidth ? `${styles.maxWidth}px` : '100%'}
        >
          <Box
            position="relative"
            {...(logoAlignment === 'left' ? {} : { transform: 'translateX(-50%)', left: '50%' })}
          >
            <Logo styles={styles} />
          </Box>
          <Flex gap="16px">
            {(props.styles?.links?.length ?? 0) > 0 && (
              <>
                <Flex
                  gap="16px"
                  css={{
                    [MOBILE_CONTAINER_QUERY]: {
                      display: 'none',
                    },
                    [DESKTOP_CONTAINER_QUERY]: {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  {props.styles?.links?.map((link: MenuItemLink) => {
                    if (link.style !== 'standard') {
                      return (
                        <PageButton
                          id={link.id}
                          key={link.id}
                          name={
                            `${link.style.charAt(0).toUpperCase()}${link.style.slice(1)}` as 'Primary' | 'Secondary'
                          }
                          asChild={true}
                          buttonText={
                            <a href={link.url} key={link.id}>
                              {link.label}
                            </a>
                          }
                        />
                      )
                    }
                    return (
                      <a href={link.url} key={link.id}>
                        {link.label}
                      </a>
                    )
                  })}
                </Flex>
                <Box
                  css={{
                    [MOBILE_CONTAINER_QUERY]: {
                      display: 'flex',
                      alignItems: 'center',
                    },
                    [DESKTOP_CONTAINER_QUERY]: {
                      display: 'none',
                    },
                  }}
                >
                  <DrawerRoot>
                    <DrawerContext>
                      {(context) => (
                        <DrawerTrigger asChild>
                          <IconButton buttonAppearance="text">
                            {context.open ? <CloseIcon /> : <TextJustifyIcon />}
                          </IconButton>
                        </DrawerTrigger>
                      )}
                    </DrawerContext>
                    <DrawerContent portalRef={headerRef}>
                      {props.styles?.links?.map((link: MenuItemLink) => (
                        <a href={link.url} key={link.id}>
                          <Box w="100%" p="16px" display="flex" justifyContent="center" key={link.id}>
                            {link.style === 'standard' ? (
                              link.label
                            ) : (
                              <PageButton
                                id={link.id}
                                name={
                                  `${link.style.charAt(0).toUpperCase()}${link.style.slice(1)}` as
                                    | 'Primary'
                                    | 'Secondary'
                                }
                                buttonText={link.label}
                              />
                            )}
                          </Box>
                        </a>
                      ))}
                    </DrawerContent>
                  </DrawerRoot>
                </Box>
              </>
            )}
            {props.styles?.hasShareButton && (
              <Box position="relative" zIndex={99}>
                <SocialShare
                  id="header-share-button"
                  {...props.styles?.shareSettings}
                  url={props.currentUrl}
                  isFullWidth={false}
                  styles={{ buttonType: 'Secondary', layoutDisplay: 'dropdown' }}
                />
              </Box>
            )}
            {props.hasDonateButton && (
              <>
                <Box
                  css={{
                    display: 'none',
                    '@media (min-width: 901px)': {
                      display: 'block',
                    },
                  }}
                >
                  <PageButton
                    id="donate-button"
                    name="Primary"
                    buttonText="Donate"
                    iconSlot={<DonateIcon />}
                    onClick={() => {
                      document
                        .querySelectorAll(`#${isMobile ? 'mobile' : 'desktop'}-layout #donation-form-element`)?.[0]
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }}
                  />
                </Box>
                <Box
                  css={{
                    display: 'block',
                    '@media (min-width: 901px)': {
                      display: 'none',
                    },
                  }}
                >
                  <PageButton
                    id="donate-button"
                    name="Primary"
                    iconSlot={<DonateIcon />}
                    onClick={() => {
                      document
                        .querySelectorAll(`#${isMobile ? 'mobile' : 'desktop'}-layout #donation-form-element`)?.[0]
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }}
                  />
                </Box>
              </>
            )}
          </Flex>
        </Flex>
      </Box>
      <Box id="ge-header-slot" w="100%" overflow="visible" />
    </Box>
  )
}

export default Header
